import React, { useState, useCallback, useEffect, useRef, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useDebounce from '../../../../../Hooks/useDebounce';
import { EditorDrawer } from './EditorDrawer';
import { EditorContent } from './EditorContent';
import { DraggableComponent } from './DraggableComponent';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';
import './ChapterEditor.scss';

export const ChapterEditor = ({ onVideoComplete, courseCompleted = false }) => {
    const { isRtl } = useContext(I18nReactContext);
    const {
        mode = "edit",
        selectedSection,
        signDocId,
        isTemplateMode,
        templateId,
        updateSection,
        addSection,
        isFullScreen,
        canvasScale,
        selectedComponentId,
        isEditorDrawerOpen,
        editingComponent,
        setCanvasScale,
        selectComponent,
        openEditorDrawer,
        closeEditorDrawer,
        updateEditingComponent,
        setFullScreen,
        isGeneratingChapter,
        isShowingAIChapterAnimations
    } = useContext(CourseEditorContext) || {};

    // Local state for components (needed for debounced updates)
    const [components, setComponents] = useState(selectedSection?.components || []);
    const [isComponentBankCollapsed, setIsComponentBankCollapsed] = useState(false);
    const [showComponentAnimations, setShowComponentAnimations] = useState(false);
    const [isSequentialAnimationActive, setIsSequentialAnimationActive] = useState(false);
    const [currentAnimatingComponentIndex, setCurrentAnimatingComponentIndex] = useState(0);
    const [animatedComponents, setAnimatedComponents] = useState(new Set());
    const componentsContainerRef = useRef(null);
    const canvasWrapperRef = useRef(null);
    const prevComponentsRef = useRef(components);
    const prevChapterIdRef = useRef(selectedSection?.id);
    // Bridge: listen to external add-component events (from side bank) and create/select the new component
    useEffect(() => {
        const handler = (e) => {
            const type = e?.detail?.type;
            if (!type || mode !== 'edit') return;

            // Handle question type differently - it's a section-level feature, not a canvas component
            if (type === 'question') {
                // Create a new section with a question (instead of modifying current section)
                const newQuestionSection = {
                    id: uuidv4(),
                    title: '', // Empty title for now, user can edit it
                    components: [], // No canvas components, this is a question section
                    question: {
                        id: uuidv4(),
                        text: '',
                        type: 'MULTIPLE_CHOICE',
                        options: [
                            { id: uuidv4(), text: '', isCorrect: false },
                            { id: uuidv4(), text: '', isCorrect: false }
                        ]
                    }
                };

                // Add the new section after the current section
                // addSection will automatically select the new section
                addSection(newQuestionSection, selectedSection?.id);

                return;
            }

            // Canvas dimensions (for canvas components only)
            const canvasWidth = componentsContainerRef.current?.clientWidth || 800;
            const canvasHeight = componentsContainerRef.current?.clientHeight || 450;

            const x = Math.floor(Math.random() * (canvasWidth - 200)) + 50;
            const y = Math.floor(Math.random() * (canvasHeight - 200)) + 50;

            const newComponent = {
                id: uuidv4(),
                type,
                content: '',
                position: components.length,
                x,
                y,
                width: 200,
                height: type === 'text' ? 'auto' : 150,
                fontSize: 16,
                bold: false,
                italic: false,
                underline: false,
                color: '#000000',
                ...(type === 'logo' && {
                    borderRadius: { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 },
                    backgroundColor: 'transparent',
                    opacity: 1
                }),
                ...(type === 'shape' && {
                    borderRadius: { topLeft: 0, topRight: 0, bottomLeft: 0, bottomRight: 0 },
                    backgroundColor: 'transparent',
                    opacity: 1,
                    shapeType: 'rectangle',
                    shapeColor: '#000000',
                    shapeOpacity: 1,
                    borderColor: '#000000',
                    borderWidth: 0,
                    backgroundOpacity: 1
                })
            };

            setComponents(prev => {
                const newComponents = [...prev, newComponent];
                // Select the new component
                selectComponent(newComponent.id);
                // Open the drawer for immediate editing
                openEditorDrawer(newComponent);
                return newComponents;
            });
        };

        window.addEventListener('weezbee:add-component', handler);
        return () => window.removeEventListener('weezbee:add-component', handler);
    }, [components.length, mode, selectComponent, openEditorDrawer, selectedSection, updateSection, addSection]);


    // Handle drag start to set the dragInProgress flag
    const handleDragStart = useCallback(() => {
        window.dragInProgress = true;
    }, []);

    // Handle animation completion for sequential animations
    const handleAnimationComplete = useCallback((componentId) => {

        setAnimatedComponents(prev => new Set([...prev, componentId]));

        // Move to the next component
        setCurrentAnimatingComponentIndex(prev => {
            const nextIndex = prev + 1;
            if (nextIndex >= components.length) {
                // All animations completed
                setIsSequentialAnimationActive(false);
                setCurrentAnimatingComponentIndex(0);
                setAnimatedComponents(new Set());
                return 0;
            }
            return nextIndex;
        });
    }, [components.length]);

    // Debounce components updates to prevent flickering
    const debouncedComponents = useDebounce({value: components, delay: 300});

    // Update components when selectedSection changes (e.g., when switching chapters)
    useEffect(() => {
        if (selectedSection && selectedSection.components) {
            // Only update if switching to a different chapter
            if (selectedSection.id !== prevChapterIdRef.current) {
                setComponents(selectedSection.components);
                prevChapterIdRef.current = selectedSection.id;
            }
        }
    }, [selectedSection]);

    // Trigger component animations when chapter changes or components are loaded
    useEffect(() => {
        const currentChapterId = selectedSection?.id;

        // Check if we should use sequential animations (during AI generation)
        const shouldUseSequentialAnimations = isGeneratingChapter &&
                                            currentChapterId &&
                                            selectedSection?.components?.length > 0;

        // Don't show regular animations if AI is currently generating or showing AI animations
        const shouldSkipRegularAnimations = isGeneratingChapter || isShowingAIChapterAnimations;

        // Simplified trigger logic: animate when we have a chapter with components and not in AI mode
        const shouldTriggerRegularAnimations = currentChapterId &&
                                             selectedSection?.components?.length > 0 &&
                                             !shouldSkipRegularAnimations;

        if (shouldUseSequentialAnimations) {
            // Start sequential animations
            setIsSequentialAnimationActive(true);
            setCurrentAnimatingComponentIndex(0);
            setAnimatedComponents(new Set());
            setShowComponentAnimations(false); // Disable regular animations
        } else if (shouldTriggerRegularAnimations) {
            // Always trigger regular animations when conditions are met
            setShowComponentAnimations(true);
            setIsSequentialAnimationActive(false);

            // Calculate total animation duration (last component delay + animation duration)
            const totalAnimationTime = (selectedSection.components.length - 1) * 200 + 500; // 200ms delay per component + 500ms animation

            // Reset animation state after all animations complete
            const timer = setTimeout(() => {
                setShowComponentAnimations(false);
            }, totalAnimationTime);

            return () => clearTimeout(timer);
        } else {
            // Ensure animations are off when conditions are not met
            setShowComponentAnimations(false);
            setIsSequentialAnimationActive(false);
        }
    }, [selectedSection?.id, selectedSection?.components?.length, isGeneratingChapter, isShowingAIChapterAnimations]);

    // Keep track of the previous selectedSection for background image comparison
    const prevChapterDataRef = useRef(selectedSection);

    // Add resize observer to handle canvas scaling
    // This effect implements the zooming functionality that:
    // 1. Preserves the width-to-height ratio of the canvas (16:9)
    // 2. Preserves the ratio between component sizes and canvas size
    // 3. Maximizes the canvas size when there's available space
    // 4. Updates the scale when the window is resized or enters/exits fullscreen mode
    useEffect(() => {
        if (!canvasWrapperRef.current || !componentsContainerRef.current) return;

        // Base dimensions of the canvas
        const baseWidth = 800;
        const baseHeight = 450;

        // Function to calculate and apply scale
        const updateCanvasScale = () => {
            if (!canvasWrapperRef.current || !componentsContainerRef.current) return;
            const wrapperRect = canvasWrapperRef.current.getBoundingClientRect();

            // Calculate available space (accounting for padding and margins)
            let availableWidth = wrapperRect.width - 40; // 20px padding on each side
            const availableHeight = wrapperRect.height - 40; // 20px padding on each side

            // In fullscreen mode, cap the available width as if sidebar is always open (300px min-width)
            // This prevents the canvas from growing when the sidebar is closed
            if (isFullScreen) {
                const sidebarWidth = 300; // Min-width of chapterPanelWrapper
                const maxAllowedWidth = window.innerWidth - sidebarWidth - 40; // Account for sidebar + padding
                availableWidth = Math.min(availableWidth, maxAllowedWidth);
            }

            // Calculate scale factors for width and height
            const scaleX = availableWidth / baseWidth;
            const scaleY = availableHeight / baseHeight;

            // Use the smaller scale to ensure the entire canvas fits
            const scale = Math.min(scaleX, scaleY);

            // Apply scale if it's valid
            if (scale > 0) {
                // Update scale in context if it changed
                if (Math.abs(scale - canvasScale) > 0.01) {
                    setCanvasScale(scale);
                }

                // Always apply the scale transform to the canvas (important when section changes)
                if (componentsContainerRef.current) {
                    componentsContainerRef.current.style.transform = `scale(${scale})`;
                }
            }
        };

        // Initial update
        updateCanvasScale();

        // When fullscreen changes, add a delayed recalculation to handle browser layout timing
        // The browser needs time to complete the fullscreen exit animation and update layout
        const delayedUpdate = setTimeout(() => {
            updateCanvasScale();
        }, 150);

        // Create resize observer
        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                updateCanvasScale();
            });
        });

        // Start observing the wrapper
        const wrapperEl = canvasWrapperRef.current;
        if (wrapperEl) resizeObserver.observe(wrapperEl);

        // Also update on window resize for fullscreen changes
        window.addEventListener('resize', updateCanvasScale);

        // Clean up
        return () => {
            clearTimeout(delayedUpdate);
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateCanvasScale);
        };
    }, [canvasScale, setCanvasScale, isFullScreen, selectedSection?.id]);

    // Update the parent component when debounced components change
    useEffect(() => {
        // Skip update if components haven't actually changed (prevents unnecessary re-renders)
        if (JSON.stringify(prevComponentsRef.current) === JSON.stringify(debouncedComponents)) return;

        // Update the ref with the current debounced value
        prevComponentsRef.current = debouncedComponents;

        // Update the parent component with the debounced components
        updateSection({
            ...selectedSection,
            components: debouncedComponents
        });
    }, [debouncedComponents, selectedSection, updateSection]);

    // Immediate update for image components and background images to ensure they appear right away
    // We use a ref to track the last time we sent an immediate update to prevent multiple rapid updates
    const lastImmediateUpdateRef = useRef(0);

    useEffect(() => {
        // Check if any image component has been updated
        const hasImageUpdate = components.some(component => 
            component.type === 'image' && 
            component.content && 
            (!prevComponentsRef.current || 
             !prevComponentsRef.current.find(c => c.id === component.id)?.content ||
             prevComponentsRef.current.find(c => c.id === component.id)?.content !== component.content)
        );

        // Check if background image has been updated
        const hasBackgroundImageUpdate = selectedSection?.backgroundImage && 
            (!prevChapterDataRef.current || 
             !prevChapterDataRef.current.backgroundImage ||
             JSON.stringify(selectedSection.backgroundImage) !== JSON.stringify(prevChapterDataRef.current.backgroundImage));

        // Check if background video has been updated
        const hasBackgroundVideoUpdate = selectedSection?.backgroundVideo && 
            (!prevChapterDataRef.current || 
             !prevChapterDataRef.current.backgroundVideo ||
             JSON.stringify(selectedSection.backgroundVideo) !== JSON.stringify(prevChapterDataRef.current.backgroundVideo));

        // If an image, background image, or background video has been updated, update the parent immediately without debouncing
        // But only if we haven't sent an update in the last 100ms to prevent multiple rapid updates
        const now = Date.now();
        if ((hasImageUpdate || hasBackgroundImageUpdate || hasBackgroundVideoUpdate) && (now - lastImmediateUpdateRef.current > 100)) {
            lastImmediateUpdateRef.current = now;
            updateSection({
                ...selectedSection,
                components
            });
        }

        // Update the previous selectedSection ref
        prevChapterDataRef.current = selectedSection;
    }, [components, selectedSection, updateSection]);

    // Handle drag end to update component position
    const handleDragEnd = useCallback((event) => {
        // Clear the dragInProgress flag
        window.dragInProgress = false;

        const { active, delta } = event;
        if (!active) return;

        setComponents(prev =>
            prev.map(component => {
                if (component.id === active.id) {
                    // Adjust delta by canvas scale to convert screen coordinates to canvas coordinates
                    const adjustedDeltaX = delta.x / canvasScale;
                    const adjustedDeltaY = delta.y / canvasScale;

                    // Calculate new position
                    const x = (component.x || 0) + adjustedDeltaX;
                    const y = (component.y || 0) + adjustedDeltaY;
                    return { ...component, x, y };
                }
                return component;
            })
        );
    }, [canvasScale]);

    // Handle canvas click to deselect components
    const handleCanvasClick = useCallback((e) => {
        // Only deselect if clicking directly on the canvas, not on a component
        if (e.target === componentsContainerRef.current) {
            selectComponent?.(null);
        }
    }, [selectComponent]);

    // Handle canvas double-click to edit background image or video
    const handleCanvasDoubleClick = useCallback((e) => {
        // Only open the background editor if we're in edit mode
        // Check if click is on the canvas container OR its direct children (not components)
        const isCanvasClick = e.target === componentsContainerRef.current ||
                             e.target.classList.contains('components-container');

        if (mode === "edit" && isCanvasClick) {
            if (selectedSection?.backgroundImage) {
                // Create a special background image component for the editor drawer
                const backgroundImageComponent = {
                    id: 'background-image',
                    type: 'backgroundImage',
                    content: selectedSection.backgroundImage.id || selectedSection.backgroundImage.signedUrl,
                    opacity: selectedSection.backgroundImage.opacity || 1,
                    backgroundColor: selectedSection.backgroundImage.backgroundColor || 'transparent'
                };
                openEditorDrawer?.(backgroundImageComponent);
            } else if (selectedSection?.backgroundVideo) {
                // Create a special background video component for the editor drawer
                const backgroundVideoComponent = {
                    id: 'background-video',
                    type: 'backgroundVideo',
                    content: selectedSection.backgroundVideo.id || selectedSection.backgroundVideo.signedUrl,
                    opacity: selectedSection.backgroundVideo.opacity || 1,
                    backgroundColor: selectedSection.backgroundVideo.backgroundColor || 'transparent'
                };
                openEditorDrawer?.(backgroundVideoComponent);
            } else {
                // No background exists - open drawer with a default backgroundImage type
                // User can switch between image and video using tabs in the drawer
                const newBackgroundComponent = {
                    id: 'background-image',
                    type: 'backgroundImage',
                    content: '',
                    opacity: 1,
                    backgroundColor: 'transparent',
                    borderRadius: {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: 0,
                        bottomRight: 0
                    }
                };
                openEditorDrawer?.(newBackgroundComponent);
            }
        }
    }, [selectedSection, mode, openEditorDrawer]);

    // Handle updating a component's content and style properties
    const handleContentChange = useCallback((id, content, styleProps = {}) => {
        // Special handling for background image component
        if (id === 'background-image') {
            if (!selectedSection) return;

            // Handle removing the background image
            if (content === null) {
                // Create a copy of the chapter data without the background image
                const updatedChapterData = { ...selectedSection };
                delete updatedChapterData.backgroundImage;
                updateSection(updatedChapterData);
                return;
            }

            // Update the chapter data with the new background image settings
            const updatedBackgroundImage = {
                ...(selectedSection.backgroundImage || {}),
                // Update the image ID if content is provided
                ...(content !== undefined && { id: content }),
                // Update signedUrl if provided in styleProps
                ...(styleProps?.signedUrl !== undefined && { signedUrl: styleProps.signedUrl }),
                // Update opacity, backgroundColor, and borderRadius if provided
                ...(styleProps?.opacity !== undefined && { opacity: styleProps.opacity }),
                ...(styleProps?.backgroundColor !== undefined && { backgroundColor: styleProps.backgroundColor }),
                ...(styleProps?.borderRadius !== undefined && { borderRadius: styleProps.borderRadius })
            };

            updateSection({
                ...selectedSection,
                backgroundImage: updatedBackgroundImage
            });

            return;
        }

        // Special handling for background video component
        if (id === 'background-video') {
            if (!selectedSection) return;

            // Handle removing the background video
            if (content === null) {
                // Create a copy of the chapter data without the background video
                const updatedChapterData = { ...selectedSection };
                delete updatedChapterData.backgroundVideo;
                updateSection(updatedChapterData);
                return;
            }

            // Update the chapter data with the new background video settings
            const updatedBackgroundVideo = {
                ...(selectedSection.backgroundVideo || {}),
                // Update the video ID if content is provided
                ...(content !== undefined && { id: content }),
                // Update signedUrl if provided in styleProps
                ...(styleProps?.signedUrl !== undefined && { signedUrl: styleProps.signedUrl }),
                // Update opacity, backgroundColor, and borderRadius if provided
                ...(styleProps?.opacity !== undefined && { opacity: styleProps.opacity }),
                ...(styleProps?.backgroundColor !== undefined && { backgroundColor: styleProps.backgroundColor }),
                ...(styleProps?.borderRadius !== undefined && { borderRadius: styleProps.borderRadius })
            };


            updateSection({
                ...selectedSection,
                backgroundVideo: updatedBackgroundVideo
            });

            return;
        }

        // Regular component handling
        setComponents(prev => 
            prev.map(component => {
                if (component.id === id) {
                    // Merge the new content and style properties with the existing component
                    return { 
                        ...component, 
                        // Only update content if it's not undefined
                        ...(content !== undefined && { content }),
                        // Add style properties if provided
                        ...(styleProps.bold !== undefined && { bold: styleProps.bold }),
                        ...(styleProps.italic !== undefined && { italic: styleProps.italic }),
                        ...(styleProps.underline !== undefined && { underline: styleProps.underline }),
                        ...(styleProps.fontSize !== undefined && { fontSize: styleProps.fontSize }),
                        ...(styleProps.color !== undefined && { color: styleProps.color }),
                        // Add logo-specific properties if provided
                        ...(styleProps.borderRadius !== undefined && { borderRadius: styleProps.borderRadius }),
                        ...(styleProps.backgroundColor !== undefined && { backgroundColor: styleProps.backgroundColor }),
                        ...(styleProps.opacity !== undefined && { opacity: styleProps.opacity }),
                        // Add direction property if provided
                        ...(styleProps.direction !== undefined && { direction: styleProps.direction }),
                        // Add shape-specific properties if provided
                        ...(styleProps.shapeType !== undefined && { shapeType: styleProps.shapeType }),
                        ...(styleProps.shapeColor !== undefined && { shapeColor: styleProps.shapeColor }),
                        ...(styleProps.shapeOpacity !== undefined && { shapeOpacity: styleProps.shapeOpacity }),
                        ...(styleProps.borderColor !== undefined && { borderColor: styleProps.borderColor }),
                        ...(styleProps.borderWidth !== undefined && { borderWidth: styleProps.borderWidth }),
                        ...(styleProps.backgroundOpacity !== undefined && { backgroundOpacity: styleProps.backgroundOpacity }),
                        // Add gradient properties if provided
                        ...(styleProps.backgroundMode !== undefined && { backgroundMode: styleProps.backgroundMode }),
                        ...(styleProps.gradientColor1 !== undefined && { gradientColor1: styleProps.gradientColor1 }),
                        ...(styleProps.gradientColor2 !== undefined && { gradientColor2: styleProps.gradientColor2 }),
                        ...(styleProps.gradientAngle !== undefined && { gradientAngle: styleProps.gradientAngle }),
                        // Add border gradient properties if provided
                        ...(styleProps.borderMode !== undefined && { borderMode: styleProps.borderMode }),
                        ...(styleProps.borderGradientColor1 !== undefined && { borderGradientColor1: styleProps.borderGradientColor1 }),
                        ...(styleProps.borderGradientColor2 !== undefined && { borderGradientColor2: styleProps.borderGradientColor2 }),
                        ...(styleProps.borderGradientAngle !== undefined && { borderGradientAngle: styleProps.borderGradientAngle }),
                    };
                }
                return component;
            })
        );
    }, [selectedSection, updateSection]);

    // Handle resizing a component
    const handleResize = useCallback((id, width, height, x, y) => {
        setComponents(prev => 
            prev.map(component => {
                if (component.id === id) {
                    return { 
                        ...component, 
                        width, 
                        height,
                        // Update position if provided
                        ...(x !== undefined && { x }),
                        ...(y !== undefined && { y })
                    };
                }
                return component;
            })
        );
    }, []);

    // Handle selecting a component
    const handleSelectComponent = useCallback((id) => {
        selectComponent?.(id);
    }, [selectComponent]);

    // Handle removing a component
    const handleRemoveComponent = useCallback((id) => {
        setComponents(prev => prev.filter(component => component.id !== id));
        if (selectedComponentId === id) {
            selectComponent?.(null);
        }
        // Close the editor drawer if the component being edited is removed
        if (editingComponent && editingComponent.id === id) {
            closeEditorDrawer?.();
        }
    }, [selectedComponentId, editingComponent, selectComponent, closeEditorDrawer]);

    // Handle duplicating a component
    const handleDuplicateComponent = useCallback((id) => {
        const componentToDuplicate = components.find(component => component.id === id);
        if (!componentToDuplicate) return;

        // Get canvas dimensions for positioning
        const canvasWidth = componentsContainerRef.current?.clientWidth || 800;
        const canvasHeight = componentsContainerRef.current?.clientHeight || 450;

        // Calculate new position (offset from original)
        const offsetX = 20;
        const offsetY = 20;
        let newX = (componentToDuplicate.x || 0) + offsetX;
        let newY = (componentToDuplicate.y || 0) + offsetY;

        // Ensure the duplicated component stays within canvas bounds
        const componentWidth = componentToDuplicate.width || 200;
        const componentHeight = componentToDuplicate.height || 150;

        if (newX + componentWidth > canvasWidth) {
            newX = Math.max(0, canvasWidth - componentWidth);
        }
        if (newY + componentHeight > canvasHeight) {
            newY = Math.max(0, canvasHeight - componentHeight);
        }

        // Create a duplicate component with a new ID and adjusted position
        const duplicatedComponent = {
            ...componentToDuplicate,
            id: uuidv4(),
            x: newX,
            y: newY,
            position: components.length // Add to end of list
        };

        // Add the duplicated component to the list
        setComponents(prev => {
            const newComponents = [...prev, duplicatedComponent];
            // Select the duplicated component
            setTimeout(() => {
                selectComponent?.(duplicatedComponent.id);
            }, 50);
            return newComponents;
        });
    }, [components, selectComponent]);

    // Handle opening the editor drawer
    const handleOpenEditorDrawer = useCallback((component) => {
        openEditorDrawer?.(component);
    }, [openEditorDrawer]);

    // Handle closing the editor drawer
    const handleCloseEditorDrawer = useCallback(() => {
        closeEditorDrawer?.();
    }, [closeEditorDrawer]);

    // Helper to update editing component (uses context)
    const setEditingComponentHelper = useCallback((component) => {
        updateEditingComponent?.(component);
    }, [updateEditingComponent]);

    // Update editingComponent when components change and drawer is open
    useEffect(() => {
        if (isEditorDrawerOpen && editingComponent) {
            // Find the updated component in the components array
            const updatedComponent = components.find(comp => comp.id === editingComponent.id);
            if (updatedComponent) {
                // Update the editingComponent state with the latest version
                setEditingComponentHelper(updatedComponent);
            }
        }
    }, [components, isEditorDrawerOpen, editingComponent, setEditingComponentHelper]);

    // Update background image component in editor drawer when selectedSection.backgroundImage changes
    useEffect(() => {
        if (isEditorDrawerOpen && editingComponent && editingComponent.type === 'backgroundImage') {
            if (selectedSection?.backgroundImage) {
                // Create an updated background image component with the latest properties
                const updatedBackgroundImageComponent = {
                    id: 'background-image',
                    type: 'backgroundImage',
                    content: selectedSection.backgroundImage.id || selectedSection.backgroundImage.signedUrl,
                    opacity: selectedSection.backgroundImage.opacity || 1,
                    backgroundColor: selectedSection.backgroundImage.backgroundColor || 'transparent',
                    borderRadius: selectedSection.backgroundImage.borderRadius || {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: 0,
                        bottomRight: 0
                    }
                };
                // Update the editingComponent state with the latest version
                setEditingComponentHelper(updatedBackgroundImageComponent);
            }
        }
    }, [selectedSection?.backgroundImage, isEditorDrawerOpen, setEditingComponentHelper]);

    // Update background video component in editor drawer when selectedSection.backgroundVideo changes
    useEffect(() => {
        if (isEditorDrawerOpen && editingComponent && editingComponent.type === 'backgroundVideo') {
            if (selectedSection?.backgroundVideo) {
                // Create an updated background video component with the latest properties
                const updatedBackgroundVideoComponent = {
                    id: 'background-video',
                    type: 'backgroundVideo',
                    content: selectedSection.backgroundVideo.id || selectedSection.backgroundVideo.signedUrl,
                    opacity: selectedSection.backgroundVideo.opacity || 1,
                    backgroundColor: selectedSection.backgroundVideo.backgroundColor || 'transparent',
                    borderRadius: selectedSection.backgroundVideo.borderRadius || {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: 0,
                        bottomRight: 0
                    }
                };
                // Update the editingComponent state with the latest version
                setEditingComponentHelper(updatedBackgroundVideoComponent);
            }
        }
    }, [selectedSection?.backgroundVideo, isEditorDrawerOpen, setEditingComponentHelper]);

    // Handle switching between background image and video in drawer
    // When user switches type, create new editing component of the opposite type
    useEffect(() => {
        if (isEditorDrawerOpen && editingComponent) {
            const isEditingBackgroundImage = editingComponent.type === 'backgroundImage';
            const isEditingBackgroundVideo = editingComponent.type === 'backgroundVideo';

            // If editing background image but chapter now has video (and no image), switch to video
            if (isEditingBackgroundImage && !selectedSection?.backgroundImage && selectedSection?.backgroundVideo) {
                const newBackgroundVideoComponent = {
                    id: 'background-video',
                    type: 'backgroundVideo',
                    content: selectedSection.backgroundVideo.id || selectedSection.backgroundVideo.signedUrl || '',
                    opacity: selectedSection.backgroundVideo.opacity || 1,
                    backgroundColor: selectedSection.backgroundVideo.backgroundColor || 'transparent',
                    borderRadius: selectedSection.backgroundVideo.borderRadius || {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: 0,
                        bottomRight: 0
                    }
                };
                setEditingComponentHelper(newBackgroundVideoComponent);
            }

            // If editing background video but chapter now has image (and no video), switch to image
            if (isEditingBackgroundVideo && !selectedSection?.backgroundVideo && selectedSection?.backgroundImage) {
                const newBackgroundImageComponent = {
                    id: 'background-image',
                    type: 'backgroundImage',
                    content: selectedSection.backgroundImage.id || selectedSection.backgroundImage.signedUrl || '',
                    opacity: selectedSection.backgroundImage.opacity || 1,
                    backgroundColor: selectedSection.backgroundImage.backgroundColor || 'transparent',
                    borderRadius: selectedSection.backgroundImage.borderRadius || {
                        topLeft: 0,
                        topRight: 0,
                        bottomLeft: 0,
                        bottomRight: 0
                    }
                };
                setEditingComponentHelper(newBackgroundImageComponent);
            }
        }
    }, [selectedSection?.backgroundImage, selectedSection?.backgroundVideo, isEditorDrawerOpen, editingComponent, setEditingComponentHelper]);

    return (
        <div className="ChapterEditor">
            <EditorContent
                components={components}
                onVideoComplete={onVideoComplete}
                courseCompleted={courseCompleted}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                handleCanvasClick={handleCanvasClick}
                handleCanvasDoubleClick={handleCanvasDoubleClick}
                handleSelectComponent={handleSelectComponent}
                handleContentChange={handleContentChange}
                handleRemoveComponent={handleRemoveComponent}
                handleDuplicateComponent={handleDuplicateComponent}
                handleResize={handleResize}
                handleOpenEditorDrawer={handleOpenEditorDrawer}
                canvasWrapperRef={canvasWrapperRef}
                componentsContainerRef={componentsContainerRef}
                showComponentAnimations={showComponentAnimations}
                isSequentialAnimationActive={isSequentialAnimationActive}
                currentAnimatingComponentIndex={currentAnimatingComponentIndex}
                animatedComponents={animatedComponents}
                handleAnimationComplete={handleAnimationComplete}
                DraggableComponent={DraggableComponent}
            />

            {/* Editor Drawer - only shown in edit mode */}
            {mode === "edit" && (
                <EditorDrawer
                    isOpen={isEditorDrawerOpen}
                    onClose={handleCloseEditorDrawer}
                    component={editingComponent}
                    onContentChange={handleContentChange}
                    isRtl={isRtl}
                    signDocId={signDocId}
                    sectionId={selectedSection?.id}
                    isTemplateMode={isTemplateMode}
                    templateId={templateId}
                />
            )}
        </div>
    );
};
